import { type QueryClient, useQuery } from '@tanstack/react-query';
import type { ReportTemplatesApi } from 'v5/api/report-templates/reportTemplates.api';
import { WithServices } from 'v5/platform/ts/services';
import { WithErrorService } from 'v5/services/front/ErrorService/ErrorService';
import { WithMessageService } from 'v5/services/internal/MessageService/MessageService';
import type { ReportTemplatesService } from './ReportTemplatesService';

const LIST_KEY = ['report-templates-v5', 'list'] as const;

export function createReportTemplatesService(
  config: WithServices<[WithErrorService, WithMessageService]> & {
    api: ReportTemplatesApi;
    queryClient: QueryClient;
  }
): ReportTemplatesService {
  const {
    api,
    queryClient,
    services: { errorService, messageService }
  } = config;
  const interceptor = errorService.withCategory('ReportTemplatesService');

  messageService.reportSystemEffect.subscribeToRequest(async message => {
    try {
      if (message.request.scope.some(e => e.entity === 'reportTemplate')) {
        await queryClient.invalidateQueries({ queryKey: LIST_KEY });
      }
    } finally {
      message.sendResponse();
    }
  });

  return {
    useTemplates: () =>
      useQuery({
        queryKey: LIST_KEY,
        queryFn: ({ signal }) =>
          interceptor.wrap(() => api.getTemplates(signal))()
      })
  };
}
